<template>
  <div class="form">
    <p class="form__title">{{ t('contact.form.title') }}</p>
    <p class="form__text">{{ t('contact.form.text') }}</p>
    <div class="form__box">
      <ui-input
          :type="'text'"
          :label="t('fields.name')"
          :rules="[required]"
          v-model="name"
      />
      <ui-input
          :type="'text'"
          :label="t('fields.phone')"
          :rules="[required]"
          v-model="phone"
      />
    </div>
    <ui-input
        :type="'email'"
        :label="t('fields.email')"
        :rules="[required]"
        v-model="email"
    />
    <ui-textarea
        :type="'text'"
        :label="t('fields.message')"
        :rules="[required]"
        v-model="message"
    />
    <ui-checkbox
        v-model="isAgree"
    >
      <i18n-t tag="p" scope="global" keypath="checkboxes.agree">
        <template #terms>
          <router-link
              class="form__link"
              :to="translations.i18nRoute({ name: 'terms' })"
          >
            {{ t('docs.terms.title') }}
          </router-link>
        </template>
        <template #policy>
          <router-link
              class="form__link"
              :to="translations.i18nRoute({ name: 'policy' })"
          >
            {{ t('docs.policy.title') }}
          </router-link>
        </template>
      </i18n-t>
    </ui-checkbox>
    <div class="form__captcha">
      <Checkbox v-model="captcha" />
    </div>
    <ui-button
      :isDisabled="!isFormValid"
      @click="contact"
    >
      {{ t('buttons.sendMessage') }}
    </ui-button>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import UiInput from "@/components/ui/ui-input.vue";
import {isEmail, required} from "@/core/rules/textFieldRules.js";
import {computed, ref} from "vue";
import UiTextarea from "@/components/ui/ui-textarea.vue";
import translations from "@/core/helpers/translations.js";
import UiCheckbox from "@/components/ui/ui-checkbox.vue";
import UiButton from "@/components/ui/ui-button.vue";
import {Processes} from "@/core/api/processes.js";
import {ElNotification} from "element-plus";
import {useAuthStore} from "@/stores/auth.js";
import {Checkbox} from "vue-recaptcha";

const {t} = useI18n()
const authStore = useAuthStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})

const name = ref('')
const phone = ref('')
const email = ref('')
const message = ref('')
const isAgree = ref(false)
const captcha = ref()

const isFormValid = computed(() => {
  return (
    required(name.value) === true &&
    required(phone.value) === true &&
    isEmail(email.value) === true &&
    required(message.value) === true &&
    isAgree.value === true &&
    captcha.value
  )
})

const contact = async () => {
  const res = await Processes.contact({
    name: name.value,
    email: email.value,
    message: message.value
  }, accessToken.value)

  if (res.errors) {
    ElNotification({
      title: t('popup.error'),
      message: res.errors[0].message,
      type: 'error'
    })
  } else {
    ElNotification({
      message: t('popup.contactSuccess'),
      type: 'success'
    })
  }
}
</script>

<style lang="scss" scoped>
.form {
  width: 535px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  border-radius: 36px;
  background-color: $white;
  box-shadow: 0 0 26px 22px rgba(255, 97, 98, 0.15);
  padding: 56px;

  &__title {
    color: #2F2F2F;
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    line-height: 28px;
  }

  &__text {
    color: #1A2B30;
    text-align: center;
    font-size: 14px;
    font-weight: 300;
    line-height: 22px;
  }

  &__box {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  &__link {
    transition: 0.2s;

    @include hover {
      color: #FF6162;
    }
  }
}

@media (max-width: 600px) {
  .form {
    width: 90%;
    padding: 30px;
  }
}

@media (max-width: 450px) {
  .form {
    padding: 20px;

    &__box {
      width: 100%;
      flex-direction: column;
      gap: 20px;
    }
  }
}
</style>